import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { RowValuesWithEcomodClass } from "../api/models/rowValues.models";
import { EcomodClass } from "../api/models/ecomodClass.models";
import { SupClass } from "../api/models/supClass.models";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../components/loadingContainer";
import { RadioButton } from "primereact/radiobutton";
import usePackages from "../hooks/usePackages";
import { Package } from "../api/models/package.models";
import useProductWithIdentifier from "../hooks/useProductWithIdentifier";
import CreateAkeneoProductForm from "./productForm";
import CreateAkeneoModelForm from "./modelForm";
import CreateAkeneoPackageForm from "./packageForm";
import { Family } from "../api/models/fullProductData.models";
import useModelWithIdentifier from "../hooks/useModelWithIdentifier";
import usePackageWithIdentifier from "../hooks/usePackageWithIdentifier";
import { SearchProduct } from "../api/models/searchProduct.models";
import usePrices from "../hooks/usePrices";
import { InfoDot } from "../components/infoDot";
import GoBackLink from "../components/goBackLink";
import { BasicProductInfo } from "../hooks/useMaterials";

const StyledView = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  background-color: #fafafa;

  & form {
    width: 100%;
    max-width: calc(100vw - var(--sidenav-width));
    flex-grow: 1;
  }

  & section {
    padding: 1rem 2rem;
  }

  .form-container {
    padding: 0 2rem;
    min-height: calc(100vh - 406px);
    width: 100%;
    max-width: 900px;
    text-align: left;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;

  & .p-radiobutton {
    margin-bottom: 2px;
  }

  & > div {
    flex: 1;
    min-width: 200px;
  }
`;

const ProductHeader = styled.h2`
  padding: 2rem 4rem;
  font-weight: 500;
  font-size: 24px;
  text-align: left;
`;

interface AkeneoProductViewProps {
  onHide: () => void;
  ecomodClasses?: EcomodClass[];
  supClasses?: SupClass[];
  defaultValues?: RowValuesWithEcomodClass[];
  selectedSearchProduct?: SearchProduct;
  reportId?: number;
  refetchSearchProducts: () => void;
}

const AkeneoProductView: React.FC<AkeneoProductViewProps> = ({
  onHide,
  ecomodClasses,
  supClasses,
  selectedSearchProduct,
  refetchSearchProducts,
}) => {
  const { t } = useTranslation();
  const [companyId] = useUIStore(
    useShallow((state: UIState) => [state.companyId]),
  );
  const [selectedType, setSelectedType] = React.useState<
    "product" | "model" | "package"
  >("product");
  const [selectMaterialSelection, setSelectMaterialSelection] = React.useState<
    "materials" | "package"
  >("materials");

  const [selectedPackage, setSelectedPackage] = React.useState<Package | null>(
    null,
  );

  // Shared name/ean/productNumber/comment/responsibleToReport fields, lifted here so
  // they aren't lost when the user switches between the product and model forms.
  // Starts undefined so it doesn't shadow existing product/model/package data via
  // the `??` fallback in the child forms when editing an existing item.
  const [basicInfoDraft, setBasicInfoDraft] = useState<
    BasicProductInfo | undefined
  >(undefined);

  const currentYear = new Date().getFullYear();
  const defaultPriceYear =
    new Date().getMonth() === 0 ? currentYear - 1 : currentYear;
  const [priceYearOverride, setPriceYearOverride] = useState<number | null>(
    null,
  );
  const priceYearOptions = Array.from(
    { length: currentYear - 2024 + 2 },
    (_, i) => ({ label: String(2024 + i), value: 2024 + i }),
  );

  const { pricesData, isLoading: isLoadingPrices } = usePrices({
    year: priceYearOverride ?? defaultPriceYear,
  });

  useEffect(() => {
    if (selectedSearchProduct?.family === Family.product) {
      setSelectedType("product");
    } else if (selectedSearchProduct?.family === Family.product_model) {
      setSelectedType("model");
    } else if (selectedSearchProduct?.family === Family.package) {
      setSelectedType("package");
    }
  }, [selectedSearchProduct]);

  const { packages, isLoading: isLoadingPackages } = usePackages({
    companyId: companyId as number,
  });

  const { productData, isLoadingProduct } = useProductWithIdentifier({
    identifier:
      selectedSearchProduct?.family === Family.product
        ? selectedSearchProduct?.identifier
        : undefined,
    companyId: companyId as number,
  });

  const { modelData, isLoadingModel } = useModelWithIdentifier({
    identifier:
      selectedSearchProduct?.family === Family.product_model
        ? selectedSearchProduct?.identifier
        : undefined,
    companyId: companyId as number,
  });

  const { packageData, isLoadingPackage } = usePackageWithIdentifier({
    identifier:
      selectedSearchProduct?.family === Family.package
        ? selectedSearchProduct?.identifier
        : undefined,
    companyId: companyId as number,
  });

  return (
    <StyledView>
      <div style={{ padding: "1rem 2rem" }}>
        <GoBackLink
          onClick={() => {
            onHide();
          }}
        >
          {`${t("go-back")}` || "← Go back"}
        </GoBackLink>
      </div>

      <ProductHeader>
        {selectedSearchProduct
          ? t("header-edit-product")
          : t("header-new-product")}
      </ProductHeader>
      <LoadingContainer
        status={
          isLoadingPackages ||
          isLoadingProduct ||
          isLoadingModel ||
          isLoadingPackage
            ? HttpRequestStatus.LOADING
            : HttpRequestStatus.SUCCESS
        }
      >
        <Row>
          <div
            style={{
              display: "flex",
              gap: "1rem",
              padding: "1rem 2.5rem 2rem 4rem",
            }}
          >
            <div>
              <RadioButton
                inputId="product-type-product"
                name="product-type"
                value="product"
                checked={selectedType === "product"}
                onChange={() => setSelectedType("product")}
                disabled={!!selectedSearchProduct}
              />
              <InfoDot
                position="right"
                text={t("infotext-product")}
                marginLeft="10px"
              >
                <label
                  htmlFor="product-type-product"
                  style={{ marginLeft: "0.5rem" }}
                >
                  {t("label-product")}
                </label>
              </InfoDot>
            </div>
            <div>
              <RadioButton
                inputId="product-type-model"
                name="product-type"
                value="model"
                checked={selectedType === "model"}
                onChange={() => setSelectedType("model")}
                disabled={!!selectedSearchProduct}
              />
              <InfoDot
                position="right"
                text={t("infotext-model")}
                marginLeft="10px"
              >
                <label
                  htmlFor="product-type-model"
                  style={{ marginLeft: "0.5rem" }}
                >
                  {t("label-model")}
                </label>
              </InfoDot>
            </div>
            <div>
              <RadioButton
                inputId="product-type-package"
                name="product-type"
                value="package"
                checked={selectedType === "package"}
                onChange={() => setSelectedType("package")}
                disabled={!!selectedSearchProduct}
              />
              <InfoDot
                position="right"
                text={t("infotext-create-multipliable-package")}
                marginLeft="10px"
              >
                <label
                  htmlFor="product-type-package"
                  style={{ marginLeft: "0.5rem" }}
                >
                  {t("label-multipliable-package")}
                </label>
              </InfoDot>
            </div>
          </div>
        </Row>
        {(selectedType === "product" || productData) && (
          <CreateAkeneoProductForm
            supClasses={supClasses}
            ecomodClasses={ecomodClasses}
            selectedSearchProduct={selectedSearchProduct}
            onHide={onHide}
            selectMaterialSelection={selectMaterialSelection}
            setSelectMaterialSelection={setSelectMaterialSelection}
            packages={packages || []}
            selectedPackage={selectedPackage}
            setSelectedPackage={setSelectedPackage}
            isLoadingPackages={isLoadingPackages}
            productData={productData}
            refetchSearchProducts={refetchSearchProducts}
            initialBasicInfo={basicInfoDraft}
            onBasicInfoChange={setBasicInfoDraft}
            pricesData={pricesData}
            useSynkkaSearch={true}
            priceYearOverride={priceYearOverride}
            setPriceYearOverride={setPriceYearOverride}
            priceYearOptions={priceYearOptions}
            priceDefaultYear={defaultPriceYear}
            isLoadingPrices={isLoadingPrices}
          />
        )}
        {(selectedType === "model" || modelData) && (
          <CreateAkeneoModelForm
            supClasses={supClasses}
            ecomodClasses={ecomodClasses}
            selectedSearchProduct={selectedSearchProduct}
            onHide={onHide}
            modelData={modelData}
            refetchSearchProducts={refetchSearchProducts}
            initialBasicInfo={basicInfoDraft}
            onBasicInfoChange={setBasicInfoDraft}
            pricesData={pricesData}
            useSynkkaSearch={true}
            priceYearOverride={priceYearOverride}
            setPriceYearOverride={setPriceYearOverride}
            priceYearOptions={priceYearOptions}
            priceDefaultYear={defaultPriceYear}
            isLoadingPrices={isLoadingPrices}
            selectMaterialSelection={selectMaterialSelection}
            setSelectMaterialSelection={setSelectMaterialSelection}
            packages={packages || []}
            selectedPackage={selectedPackage}
            setSelectedPackage={setSelectedPackage}
            isLoadingPackages={isLoadingPackages}
          />
        )}
        {(selectedType === "package" || packageData) && (
          <CreateAkeneoPackageForm
            supClasses={supClasses}
            ecomodClasses={ecomodClasses}
            selectedSearchProduct={selectedSearchProduct}
            onHide={onHide}
            packageData={packageData}
            refetchSearchProducts={refetchSearchProducts}
            initialBasicInfo={basicInfoDraft}
            onBasicInfoChange={setBasicInfoDraft}
            pricesData={pricesData}
            priceYearOverride={priceYearOverride}
            setPriceYearOverride={setPriceYearOverride}
            priceYearOptions={priceYearOptions}
            priceDefaultYear={defaultPriceYear}
            isLoadingPrices={isLoadingPrices}
          />
        )}
      </LoadingContainer>
    </StyledView>
  );
};

export default AkeneoProductView;
